import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { route } from '../../../services/utils';
import { useService } from '../../../services/services';
import Form, { SubmitType } from '../../../components/form';
import Group from '../../../components/form/elements/group';
import Text from '../../../components/form/elements/text';
import Checkbox from '../../../components/form/elements/checkbox';
import Button from '../../../components/form/elements/button';
import Link from '../../../components/form/elements/link';
import { FARM_USERS_INDEX, FARM_USERS_VIEW } from '../../../router/routes';
import { FarmUser } from '../../../interfaces/farm-user';
import { useObservableState } from 'observable-hooks';
import Abilities from './abilities';
import TextArea from '../../../components/form/elements/text-area';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import Disabled from '../../../components/form/elements/disabled';
import * as yup from 'yup';
import Watch from '../../../components/form/elements/watch';
import Show from '../../../components/form/elements/show';

export const userSchema = yup.object().shape({
  name: yup.string().max(255).required(),
  email: yup.string().max(255).email().required(),
  email_confirmation: yup.string().max(255).email().required(),
  personal_code: yup
    .string()
    .matches(/^\d{11}$/, 'The personal code must be 11 digits long!')
    .required(),
  owner: yup.boolean(),
  main_user: yup.boolean(),
  blocked: yup.boolean(),
  comment: yup.string().nullable(),
});

export default function FarmCreateUser() {
  const { farmId } = useParams<{ farmId: string }>();
  const { t } = useTranslation();
  const history = useHistory();
  const { farmUserService } = useService();
  const { selectedFarm } = useSelector((state: RootState) => ({
    selectedFarm: state.global.farm,
  })); // Refactor to loading in the farm

  const [bodyElements, setBodyElements] = useState([]);
  const [footerElements] = useState(() => [
    Button('create'),
    Link(route(FARM_USERS_INDEX, { farmId }), 'cancel'),
  ]);

  const [abilities] = useObservableState<any>(
    () => farmUserService.abilities(farmId),
    null
  );
  const [selectedAbilities, setSelectedAbilities] = useState({});

  const [values, setValues] = useState({});
  useEffect(() => {
    setValues((v) => ({ ...v, farm_name: selectedFarm.name }));
  }, [selectedFarm, setValues]);

  useEffect(() => {
    if (abilities) {
      setBodyElements([
        Group([
          Disabled(Text('farm_name', t('pages.farm.users.farm_name'))),
          Text('name', t('pages.farm.users.name')),
          Text('email', t('pages.farm.users.email')),
          Text('email_confirmation', t('pages.farm.users.email_confirmation')),
          Watch(
            Text('personal_code', t('pages.farm.users.personal_code')),
            ({ value, setValue }) => {
              setValue('personal_code', value.replace(/\D/g, ''));
            }
          ),
          Disabled(Checkbox('owner', t('pages.farm.users.owner'))),
          Checkbox('main_user', t('pages.farm.users.main_user')),
          Checkbox('blocked', t('pages.farm.users.blocked')),
          TextArea('comment', t('pages.farm.users.comment')),
          Show(
            Abilities(abilities, {}, (selectedAbilities) =>
              setSelectedAbilities(selectedAbilities)
            ),
            'main_user',
            (main_user: boolean) => !main_user
          ),
        ]),
      ]);
    }
  }, [t, abilities, setSelectedAbilities]);

  const submit = useCallback<SubmitType<FarmUser>>(
    (form) => {
      const formData = { ...form, selectedAbilities };
      delete formData.abilities;

      return [
        farmUserService.store(farmId, formData),
        (response: FarmUser) =>
          history.push(route(FARM_USERS_VIEW, { farmId, id: response.id })),
      ];
    },
    [farmUserService, history, farmId, selectedAbilities]
  );

  return (
    <Form
      mode={'create'}
      title={t('pages.farm.users.create-user')}
      backTo={null}
      body={bodyElements}
      footer={footerElements}
      values={values}
      submit={submit}
      schema={userSchema}
      noticeAccessor="CreateForm"
    />
  );
}
